import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Download, ShieldCheck, Lock, Search, FilterX, Loader2, PlusCircle, Trash2, Edit, X, CalendarDays } from 'lucide-react';
import axios from 'axios';
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  "Toate serviciile",
  "Comandă plăcuțe numere",
  "Programare la proba practică",
  "Programare proba teoretică (reducere suspendare)",
  "Înmatriculare / Transcriere auto",
  "Preschimbare permis conducere"
];

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  const [reservations, setReservations] = useState([]);
  const [news, setNews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterService, setFilterService] = useState('Toate serviciile');
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [newsTitle, setNewsTitle] = useState('');
  const [newsDate, setNewsDate] = useState('');
  const [newsSummary, setNewsSummary] = useState('');
  const [newsContent, setNewsContent] = useState('');

  const handleLogin = (e) => {
    e.preventDefault();
    if (email === 'administrator@srpcivgorj.ro' && password === 'admin') {
      setIsAuthenticated(true);
      fetchNews();
    } else {
      toast.error("Credențiale incorecte!");
    }
  };

  const fetchReservations = async () => {
    if (!startDate || !endDate) {
      toast.error("Filtru Incomplet", { description: "Pentru a preveni supraîncărcarea sistemului, selectarea intervalului (De la data - Până la data) este OBLIGATORIE." });
      return;
    }

    if (startDate > endDate) {
      toast.error("Eroare Logică", { description: "Data de început nu poate fi mai mare decât data de sfârșit." });
      return;
    }

    setIsLoading(true);
    try {
      let url = `${API}/rezervari?start_date=${startDate}&end_date=${endDate}&`;
      if (filterService !== 'Toate serviciile') url += `service=${encodeURIComponent(filterService)}`;
      
      const response = await axios.get(url);
      setReservations(response.data);
      setHasSearched(true);
    } catch (error) {
      toast.error("Eroare la preluarea rezervărilor din baza de date.");
    } finally {
      setIsLoading(false);
    }
  };

  // [CRITIC] NOU: Funcția de apelare a ștergerii pentru rezervări
  const handleDeleteReservation = async (id) => {
    if(window.confirm("Sigur doriți să anulați/ștergeți această programare din baza de date? Acțiunea este ireversibilă!")) {
      try {
        await axios.delete(`${API}/rezervari/${id}`);
        toast.success("Programarea a fost ștearsă cu succes!");
        fetchReservations(); // Reîncărcăm lista automat
      } catch (error) {
        toast.error("Eroare la ștergerea programării.");
      }
    }
  };

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/noutati`);
      setNews(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownloadCSV = () => {
    let url = `${API}/admin/rezervari/export?start_date=${startDate}&end_date=${endDate}&`;
    if (filterService !== 'Toate serviciile') url += `service=${encodeURIComponent(filterService)}`;
    window.open(url, '_blank');
  };

  const resetFilters = () => {
    setStartDate('');
    setEndDate('');
    setFilterService('Toate serviciile');
    setSearchTerm('');
    setReservations([]);
    setHasSearched(false);
  };

  const resetNewsForm = () => {
    setEditingId(null);
    setNewsTitle('');
    setNewsDate('');
    setNewsSummary('');
    setNewsContent('');
  };

  const handleEditClick = (item) => {
    setEditingId(item.id);
    setNewsTitle(item.title);
    setNewsDate(item.date);
    setNewsSummary(item.summary);
    setNewsContent(item.content);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveNews = async (e) => {
    e.preventDefault();
    const payload = {
      title: newsTitle,
      date: newsDate,
      summary: newsSummary,
      content: newsContent
    };

    try {
      if (editingId) {
        await axios.put(`${API}/noutati/${editingId}`, payload);
        toast.success("Articolul a fost actualizat!");
      } else {
        await axios.post(`${API}/noutati`, payload);
        toast.success("Articolul a fost publicat!");
      }
      resetNewsForm();
      fetchNews();
    } catch (error) {
      toast.error("Eroare la salvarea articolului.");
    }
  };

  const handleDeleteNews = async (id) => {
    if(window.confirm("Sigur doriți să ștergeți acest articol?")) {
      try {
        await axios.delete(`${API}/noutati/${id}`);
        toast.success("Articol șters!");
        if (editingId === id) resetNewsForm();
        fetchNews();
      } catch (error) {
        toast.error("Eroare la ștergerea articolului.");
      }
    }
  };

  const filteredReservations = reservations.filter(r => 
    r.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.cnp.includes(searchTerm)
  );

  const formatSystemDate = (dateString) => {
    if (!dateString) return null;
    const utcString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
    const d = new Date(utcString);
    const formattedDate = d.toLocaleDateString('en-CA'); 
    const formattedTime = d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
    return { date: formattedDate, time: formattedTime };
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center animate-in fade-in duration-500">
        <Card className="w-full max-w-md shadow-lg border-slate-200">
          <CardHeader className="bg-slate-900 text-white rounded-t-lg text-center pb-6">
            <div className="mx-auto bg-slate-800 p-3 rounded-full w-fit mb-4">
              <Lock className="w-8 h-8 text-slate-300" />
            </div>
            <CardTitle className="text-2xl">Autentificare Administrator</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">E-mail administrator</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Parolă</label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white mt-4">
                Intră în Panou
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-4">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-8 h-8 text-green-600" />
          <h1 className="text-3xl font-bold text-slate-800">Panou Administrator</h1>
        </div>
      </div>

      <Tabs defaultValue="rezervari" className="w-full">
        <TabsList className="mb-6 bg-slate-100 p-1">
          <TabsTrigger value="rezervari" className="px-6 data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm font-medium">Gestionare Programări</TabsTrigger>
          <TabsTrigger value="noutati" className="px-6 data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm font-medium">Postare / Editare Noutăți</TabsTrigger>
        </TabsList>

        <TabsContent value="rezervari" className="space-y-6">
          <Card className="bg-slate-50 border-slate-200">
            <CardHeader className="py-3 px-4 border-b border-slate-100 bg-white rounded-t-xl">
              <CardTitle className="text-sm font-bold flex items-center gap-2 text-slate-700 uppercase tracking-wide">
                <CalendarDays className="w-4 h-4 text-blue-600" /> Filtrare Bază de Date
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 flex flex-col gap-4">
              <div className="flex flex-col md:flex-row gap-4 items-end w-full">
                <div className="flex flex-col sm:flex-row gap-3 flex-[1.5]">
                  <div className="space-y-1.5 w-full">
                    <label className="text-xs font-semibold text-slate-600 uppercase">De la data: *</label>
                    <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-white" required />
                  </div>
                  <div className="space-y-1.5 w-full">
                    <label className="text-xs font-semibold text-slate-600 uppercase">Până la data: *</label>
                    <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-white" required />
                  </div>
                </div>

                <div className="space-y-1.5 flex-[2]">
                  <label className="text-xs font-semibold text-slate-600 uppercase">Tipul Serviciului</label>
                  <Select value={filterService} onValueChange={setFilterService}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Toate serviciile" /></SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto">
                  <Button onClick={fetchReservations} className="bg-blue-600 hover:bg-blue-700 text-white flex-1 md:flex-none flex items-center justify-center gap-2 px-6 shadow-sm">
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    Caută
                  </Button>
                  <Button onClick={resetFilters} variant="outline" className="flex items-center justify-center gap-2 text-slate-600 px-4">
                    <FilterX className="w-4 h-4" /> Reset
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm border-slate-200">
            <CardHeader className="bg-slate-50 border-b border-slate-100 flex flex-col md:flex-row justify-between items-center gap-4">
              <CardTitle className="text-lg text-slate-800">
                {hasSearched ? `Rezultate Găsite: ${filteredReservations.length}` : 'Așteptare interogare...'}
              </CardTitle>
              <div className="flex gap-3 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input placeholder="Filtru rapid (nume/cnp)..." className="pl-9 bg-white" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} disabled={!hasSearched} />
                </div>
                <Button onClick={handleDownloadCSV} disabled={!hasSearched || filteredReservations.length === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0">
                  <Download className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">Descarcă Excel (.xlsx)</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto min-h-[300px]">
                {isLoading ? (
                  <div className="flex justify-center items-center py-20 text-blue-600"><Loader2 className="w-8 h-8 animate-spin" /></div>
                ) : (
                  <Table>
                    <TableHeader className="bg-slate-100">
                      <TableRow>
                        <TableHead className="font-bold text-slate-700 w-24">Data/Ora<br/>(Ghișeu)</TableHead>
                        <TableHead className="font-bold text-slate-700 w-48">Tip Serviciu</TableHead>
                        <TableHead className="font-bold text-slate-700">Nume Solicitant</TableHead>
                        <TableHead className="font-bold text-slate-700">CNP</TableHead>
                        <TableHead className="font-bold text-slate-700">E-mail</TableHead>
                        <TableHead className="font-bold text-slate-700 text-center w-28">Înregistrare<br/>(Sistem)</TableHead>
                        <TableHead className="font-bold text-slate-700 text-center w-16">Acțiuni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!hasSearched ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-16 text-slate-500 font-medium">
                            Obligatoriu: Selectați intervalul calendaristic (De la - Până la) și apăsați <strong className="text-blue-600">"Caută"</strong>.
                          </TableCell>
                        </TableRow>
                      ) : filteredReservations.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-16 text-red-500 font-medium">
                            Nu au fost găsite programări în intervalul selectat.
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredReservations.map((res) => {
                          const sysDate = formatSystemDate(res.created_at);
                          return (
                            <TableRow key={res.id} className="hover:bg-blue-50/50">
                              <TableCell className="font-medium text-blue-700 whitespace-nowrap align-middle">
                                {res.date} <br/>
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full inline-block mt-1">{res.time}</span>
                              </TableCell>
                              <TableCell className="text-slate-700 font-medium text-[11px] leading-tight whitespace-normal max-w-[160px] align-middle">
                                {res.serviceType}
                              </TableCell>
                              <TableCell className="text-sm whitespace-nowrap align-middle">{res.lastName} {res.firstName}</TableCell>
                              <TableCell className="text-slate-600 font-mono text-sm whitespace-nowrap align-middle">{res.cnp}</TableCell>
                              <TableCell className="text-slate-600 text-[13px] whitespace-nowrap align-middle">{res.email}</TableCell>
                              <TableCell className="text-center whitespace-nowrap align-middle">
                                {sysDate ? (
                                  <>
                                    <span className="font-medium text-slate-600">{sysDate.date}</span> <br/>
                                    <span className="text-[11px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded border border-slate-200 inline-block mt-1">{sysDate.time}</span>
                                  </>
                                ) : 'N/A'}
                              </TableCell>
                              <TableCell className="text-center align-middle">
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteReservation(res.id)} className="text-red-500 hover:bg-red-50 hover:text-red-700" title="Șterge Programare">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          )
                        })
                      )}
                    </TableBody>
                  </Table>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="noutati" className="space-y-6">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className={`md:col-span-1 shadow-sm h-fit transition-colors ${editingId ? 'border-amber-400 bg-amber-50/10' : 'border-slate-200'}`}>
              <CardHeader className={`${editingId ? 'bg-amber-100/50' : 'bg-slate-50'} border-b border-slate-100 flex flex-row justify-between items-center`}>
                <CardTitle className={`text-lg flex items-center gap-2 ${editingId ? 'text-amber-800' : 'text-slate-800'}`}>
                  {editingId ? <Edit className="w-5 h-5" /> : <PlusCircle className="w-5 h-5 text-blue-600" />}
                  {editingId ? 'Editare Articol' : 'Adaugă Articol Nou'}
                </CardTitle>
                {editingId && (
                  <Button variant="ghost" size="sm" onClick={resetNewsForm} className="text-slate-500 hover:text-slate-800">
                    <X className="w-4 h-4 mr-1"/> Anulează
                  </Button>
                )}
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handleSaveNews} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Titlu Articol *</label>
                    <Input value={newsTitle} onChange={e => setNewsTitle(e.target.value)} required placeholder="Ex: Informare privind plățile..." />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Data *</label>
                    <Input type="date" value={newsDate} onChange={e => setNewsDate(e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Rezumat scurt *</label>
                    <Textarea value={newsSummary} onChange={e => setNewsSummary(e.target.value)} required placeholder="Un text de introducere de 1-2 propoziții..." rows={3} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Conținut complet *</label>
                    <Textarea value={newsContent} onChange={e => setNewsContent(e.target.value)} required placeholder="Conținutul integral al anunțului..." rows={8} />
                  </div>
                  <Button type="submit" className={`w-full text-white ${editingId ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                    {editingId ? 'Salvează Modificările' : 'Publică Noutatea'}
                  </Button>
                </form>
              </CardContent>
            </Card>
            <Card className="md:col-span-2 shadow-sm border-slate-200">
              <CardHeader className="bg-slate-50 border-b border-slate-100">
                <CardTitle className="text-lg text-slate-800">Articole Publicate ({news.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-y-auto max-h-[700px]">
                  <Table>
                    <TableBody>
                      {news.length === 0 ? (
                        <TableRow><TableCell className="text-center py-8 text-slate-500">Niciun articol publicat.</TableCell></TableRow>
                      ) : (
                        news.map((item) => (
                          <TableRow key={item.id} className={editingId === item.id ? 'bg-amber-50' : ''}>
                            <TableCell className="align-top py-4">
                              <div className="font-semibold text-blue-800 mb-1">{item.title}</div>
                              <div className="text-xs font-bold text-slate-400 mb-2">{item.date}</div>
                              <div className="text-sm text-slate-600 line-clamp-2">{item.summary}</div>
                            </TableCell>
                            <TableCell className="align-middle text-right w-24">
                              <div className="flex flex-col gap-2">
                                <Button variant="outline" size="sm" onClick={() => handleEditClick(item)} className="border-blue-200 text-blue-700 hover:bg-blue-50">
                                  <Edit className="w-4 h-4 mr-1" /> Edit
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => handleDeleteNews(item.id)} className="border-red-200 text-red-600 hover:bg-red-50">
                                  <Trash2 className="w-4 h-4 mr-1" /> Șterge
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}